import React from 'react';
import {
  Box,
  IconButton,
  InputBase,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Tooltip,
} from '@mui/material';
import { fetchKnrRms } from '../../services/knrService';
import { parseKnrAndSymbolFromPodstawa } from '../../utils/knrPodstawaParse';
/** Liczba wierszy mini-arkusza (nagłówki 1…N w lewej kolumnie). */
export const SCRATCH_ROWS = 50;
export const SCRATCH_COLS = 5;
const COL_LABELS = ['A', 'B', 'C', 'D', 'E'] as const;

/** 24 kolory pod ikonami wypełnienia/tekstu: neutrale, pastele, żywe i ciemniejsze odcienie (siatka 6×4). */
const SCRATCH_COLOR_PALETTE: string[] = [
  '#202124',
  '#5f6368',
  '#9aa0a6',
  '#dadce0',
  '#f1f3f4',
  '#ffffff',
  '#fce8e6',
  '#fff3e0',
  '#fff9c4',
  '#e3f2fd',
  '#e8f5e9',
  '#f3e5f5',
  '#ea4335',
  '#f9ab00',
  '#34a853',
  '#1a73e8',
  '#9c27b0',
  '#ff6d00',
  '#c62828',
  '#ef6c00',
  '#2e7d32',
  '#1565c0',
  '#6a1b9a',
  '#37474f',
];

/** Ikony z Material Symbols (Google Fonts) — link w `public/index.html`. */
const ScratchMaterialIcon: React.FC<{ name: string; fontSize?: number }> = ({ name, fontSize = 18 }) => (
  <Box
    component="span"
    className="material-symbols-outlined"
    sx={{
      fontFamily: '"Material Symbols Outlined"',
      fontWeight: 400,
      fontStyle: 'normal',
      lineHeight: 1,
      display: 'inline-block',
      fontVariationSettings: "'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 20",
      fontSize,
    }}
    aria-hidden
  >
    {name}
  </Box>
);

export type ScratchCellStyle = {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  backgroundColor?: string;
  color?: string;
};

function scratchCellKey(r: number, c: number): string {
  return `${r},${c}`;
}

const ROW_GUTTER_PX = 28;
const MIN_DATA_COL_PX = 40;
const PANEL_WIDTH_MIN = 260;
const PANEL_WIDTH_MAX = 640;
const PANEL_WIDTH_DEFAULT = 320;
const LS_COL_WIDTHS = 'easyEstimate.pozycjaScratchColWidths';
const LS_PANEL_WIDTH = 'easyEstimate.pozycjaScratchPanelWidth';

export const POZYCJA_SCRATCHPAD_WIDTH_PX = PANEL_WIDTH_DEFAULT;

function loadColWidths(): number[] {
  const fallback = () =>
    Array.from({ length: SCRATCH_COLS }, () =>
      Math.max(MIN_DATA_COL_PX, Math.floor((PANEL_WIDTH_DEFAULT - ROW_GUTTER_PX - 24) / SCRATCH_COLS))
    );
  try {
    const raw = localStorage.getItem(LS_COL_WIDTHS);
    if (!raw) return fallback();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed) || parsed.length !== SCRATCH_COLS) return fallback();
    const nums = parsed.map((x) => (typeof x === 'number' && Number.isFinite(x) ? x : MIN_DATA_COL_PX));
    return nums.map((w) => Math.max(MIN_DATA_COL_PX, Math.min(400, w)));
  } catch {
    return fallback();
  }
}

function loadPanelWidth(): number {
  try {
    const raw = localStorage.getItem(LS_PANEL_WIDTH);
    if (raw == null) return PANEL_WIDTH_DEFAULT;
    const n = Number(raw);
    if (!Number.isFinite(n)) return PANEL_WIDTH_DEFAULT;
    return Math.min(PANEL_WIDTH_MAX, Math.max(PANEL_WIDTH_MIN, Math.round(n)));
  } catch {
    return PANEL_WIDTH_DEFAULT;
  }
}

export function makeEmptyScratchGrid(): string[][] {
  return Array.from({ length: SCRATCH_ROWS }, () => Array(SCRATCH_COLS).fill(''));
}

export function makeEmptyScratchStyles(): ScratchCellStyle[][] {
  return Array.from({ length: SCRATCH_ROWS }, () => Array.from({ length: SCRATCH_COLS }, () => ({})));
}

/** Stałe arkusze mini-kalkulatora (jak zakładki u dołu w Google Sheets). */
export const SCRATCH_SHEET_TABS = [
  { id: 'wlasne' as const, label: 'Własne' },
  { id: 'knr' as const, label: 'KNR' },
  { id: 'gemma' as const, label: 'Gemma' },
] as const;
export type ScratchSheetId = (typeof SCRATCH_SHEET_TABS)[number]['id'];
export type CalcScratchSheetData = { grid: string[][]; cellStyles: ScratchCellStyle[][] };
export type CalcScratchBundle = Record<ScratchSheetId, CalcScratchSheetData>;

export function makeEmptyScratchBundle(): CalcScratchBundle {
  return {
    wlasne: { grid: makeEmptyScratchGrid(), cellStyles: makeEmptyScratchStyles() },
    knr: { grid: makeEmptyScratchGrid(), cellStyles: makeEmptyScratchStyles() },
    gemma: { grid: makeEmptyScratchGrid(), cellStyles: makeEmptyScratchStyles() },
  };
}

/** Ujednolica siatkę z API / stanu (np. stary zapis 12×4 lub 50×4) do aktualnego rozmiaru. */
export function normalizeScratchGridProp(grid: string[][]): string[][] {
  const rows: string[][] = [];
  for (let r = 0; r < SCRATCH_ROWS; r++) {
    const src = grid[r];
    const row: string[] = [];
    for (let c = 0; c < SCRATCH_COLS; c++) {
      row.push(src && src[c] != null ? String(src[c]) : '');
    }
    rows.push(row);
  }
  return rows;
}

function scratchGridDimensionsOk(g: string[][]): boolean {
  return g.length === SCRATCH_ROWS && g.every((row) => Array.isArray(row) && row.length === SCRATCH_COLS);
}

function normalizeScratchCellStylesProp(styles: ScratchCellStyle[][]): ScratchCellStyle[][] {
  const rows: ScratchCellStyle[][] = [];
  for (let r = 0; r < SCRATCH_ROWS; r++) {
    const src = styles[r];
    const row: ScratchCellStyle[] = [];
    for (let c = 0; c < SCRATCH_COLS; c++) {
      const o = src && src[c];
      row.push(o && typeof o === 'object' ? { ...o } : {});
    }
    rows.push(row);
  }
  return rows;
}

function scratchCellStylesDimensionsOk(styles: ScratchCellStyle[][]): boolean {
  return (
    styles.length === SCRATCH_ROWS &&
    styles.every((row) => Array.isArray(row) && row.length === SCRATCH_COLS)
  );
}

function formatA1(r: number, c: number): string {
  if (c < 0 || c >= SCRATCH_COLS || r < 0 || r >= SCRATCH_ROWS) return '';
  return `${COL_LABELS[c]}${r + 1}`;
}

function formatRangeA1(a: { r: number; c: number }, b: { r: number; c: number }): string {
  const rLo = Math.min(a.r, b.r);
  const rHi = Math.max(a.r, b.r);
  const cLo = Math.min(a.c, b.c);
  const cHi = Math.max(a.c, b.c);
  const p1 = formatA1(rLo, cLo);
  const p2 = formatA1(rHi, cHi);
  if (p1 === p2) return p1;
  return `${p1}:${p2}`;
}

/** Fragment odwołania A1 lub A1:B2 po nawiasie Suma( — pusty lub pojedyncza komórka lub zakres. */
const SUMA_REF_TAIL_RE =
  /^\s*([A-Ea-e](?:[1-4][0-9]|[1-9]|50)(?::[A-Ea-e](?:[1-4][0-9]|[1-9]|50))?)?\s*$/i;

/** Kursor w polu formuły: tuż po =Suma( lub z częściowym zakresem — można zaznaczać zakres myszą. */
function isSumaRangePickingBeforeCursor(val: string, cursor: number): boolean {
  const before = val.slice(0, Math.max(0, cursor));
  const openRe = /=\s*Suma\s*\(\s*/gi;
  let lastOpenEnd = -1;
  let m: RegExpExecArray | null;
  while ((m = openRe.exec(before)) !== null) {
    lastOpenEnd = m.index + m[0].length;
  }
  if (lastOpenEnd < 0) return false;
  const refPart = before.slice(lastOpenEnd);
  return SUMA_REF_TAIL_RE.test(refPart);
}

function iterRect(
  a: { r: number; c: number },
  b: { r: number; c: number },
  fn: (r: number, c: number) => void
): void {
  const r0 = Math.min(a.r, b.r);
  const r1 = Math.max(a.r, b.r);
  const c0 = Math.min(a.c, b.c);
  const c1 = Math.max(a.c, b.c);
  for (let r = r0; r <= r1; r++) {
    for (let c = c0; c <= c1; c++) {
      fn(r, c);
    }
  }
}

/** Wstawia / podmienia zakres A1 w ostatnim wywołaniu Suma( przed kursorem. */
function patchSumaRangeInFormula(val: string, cursor: number, rangeA1: string, appendCloseParen: boolean): string | null {
  const before = val.slice(0, cursor);
  const after = val.slice(cursor);
  const openRe = /=\s*Suma\s*\(\s*/gi;
  let lastOpenEnd = -1;
  let m: RegExpExecArray | null;
  while ((m = openRe.exec(before)) !== null) {
    lastOpenEnd = m.index + m[0].length;
  }
  if (lastOpenEnd < 0) return null;
  const refPart = before.slice(lastOpenEnd);
  if (!SUMA_REF_TAIL_RE.test(refPart)) return null;
  const prefix = before.slice(0, lastOpenEnd);
  let mid = rangeA1;
  if (appendCloseParen) mid += ')';
  return prefix + mid + after;
}

function getActiveScratchFormulaInput(
  formulaBarEl: HTMLInputElement | null,
  cellMap: Map<string, HTMLInputElement>,
  selected: { r: number; c: number } | null
): { el: HTMLInputElement; cellR: number; cellC: number } | null {
  const ae = document.activeElement;
  if (!(ae instanceof HTMLInputElement)) return null;
  if (formulaBarEl && ae === formulaBarEl) {
    if (!selected) return null;
    return { el: ae, cellR: selected.r, cellC: selected.c };
  }
  for (const [k, el] of Array.from(cellMap.entries())) {
    if (el === ae) {
      const [rs, cs] = k.split(',');
      return { el: ae, cellR: parseInt(rs, 10), cellC: parseInt(cs, 10) };
    }
  }
  return null;
}

/** Ta sama logika co w przedmiarze — do wstawienia wyniku w „Cena jedn.” */
function parseLoosePlDecimal(raw: string): number | null {
  const t = raw.trim().replace(/\u00a0/g, '').replace(/\s/g, '');
  if (!t) return null;
  const lastComma = t.lastIndexOf(',');
  const lastDot = t.lastIndexOf('.');
  let normalized: string;
  if (lastComma === -1 && lastDot === -1) {
    normalized = t;
  } else if (lastComma > lastDot) {
    const intPart = t.slice(0, lastComma).replace(/\./g, '');
    const fracPart = t.slice(lastComma + 1).replace(/,/g, '');
    normalized = `${intPart}.${fracPart}`;
  } else {
    normalized = t.replace(/,/g, '');
  }
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

/** Wiersze 1–50, kolumny A–E (jak nagłówki arkusza). */
const CELL_REF_ROW = '(?:[1-4][0-9]|50|[1-9])';
const CELL_REF_RE = new RegExp(`\\b([A-Ea-e])(${CELL_REF_ROW})\\b`, 'g');

function colLetterToIndex(letter: string): number {
  const L = letter.toUpperCase();
  return COL_LABELS.indexOf(L as (typeof COL_LABELS)[number]);
}

const MAX_FORMULA_DEPTH = 48;

/**
 * Wartość liczbowa komórki do użycia w formule (pusta → 0).
 * Formuła w komórce — rekurencyjnie; `visiting` wykrywa cykle.
 */
function getNumericCellValueForFormula(
  grid: string[][],
  rowIdx: number,
  colIdx: number,
  visiting: Set<string>,
  depth: number
): number | null {
  if (depth > MAX_FORMULA_DEPTH) return null;
  if (rowIdx < 0 || rowIdx >= SCRATCH_ROWS || colIdx < 0 || colIdx >= SCRATCH_COLS) return null;
  const key = scratchCellKey(rowIdx, colIdx);
  if (visiting.has(key)) return null;
  const raw = (grid[rowIdx]?.[colIdx] ?? '').trim();
  if (raw === '') return 0;
  if (raw.startsWith('=')) {
    visiting.add(key);
    try {
      return evaluateFormulaBody(grid, raw.slice(1).trim().replace(/,/g, '.'), visiting, depth + 1);
    } finally {
      visiting.delete(key);
    }
  }
  const n = parseLoosePlDecimal(raw);
  if (n === null) return null;
  return n;
}

function sumRectInGrid(
  grid: string[][],
  r1: number,
  c1: number,
  r2: number,
  c2: number,
  visiting: Set<string>,
  depth: number
): number | null {
  const rLo = Math.min(r1, r2);
  const rHi = Math.max(r1, r2);
  const cLo = Math.min(c1, c2);
  const cHi = Math.max(c1, c2);
  let total = 0;
  for (let r = rLo; r <= rHi; r++) {
    for (let c = cLo; c <= cHi; c++) {
      const v = getNumericCellValueForFormula(grid, r, c, visiting, depth);
      if (v === null) return null;
      total += v;
    }
  }
  return total;
}

/** Suma(B1) lub Suma(B1:B5) — wielkość liter bez znaczenia. */
const SUMA_CALL_RE = new RegExp(
  `\\bSuma\\s*\\(\\s*([A-Ea-e])\\s*(${CELL_REF_ROW})(?:\\s*:\\s*([A-Ea-e])\\s*(${CELL_REF_ROW}))?\\s*\\)`,
  'gi'
);

/**
 * Przesuwa pojedyncze odwołanie A1 o (dr, dc) — dr/dc to różnica wierszy/kolumn 0-based między komórką docelową a źródłową
 * (jak względne kopiowanie formuły w Excelu / Arkuszach).
 */
function shiftCellRefToken(colLetter: string, rowStr: string, dr: number, dc: number): string {
  const c0 = colLetterToIndex(colLetter);
  const r1 = parseInt(rowStr, 10);
  if (c0 < 0 || !Number.isFinite(r1)) return `${String(colLetter).toUpperCase()}${rowStr}`;
  const nc = c0 + dc;
  const nr = r1 + dr;
  if (nc < 0 || nc >= SCRATCH_COLS || nr < 1 || nr > SCRATCH_ROWS) {
    return '#REF!';
  }
  return `${COL_LABELS[nc]}${nr}`;
}

/** Przesuwa wszystkie odwołania A1 w ciele formuły (bez znaku =), także wewnątrz Suma(…). */
function shiftFormulaBody(body: string, dr: number, dc: number): string {
  if (dr === 0 && dc === 0) return body;
  CELL_REF_RE.lastIndex = 0;
  return body.replace(CELL_REF_RE, (_m, colLetter: string, rowStr: string) =>
    shiftCellRefToken(colLetter, rowStr, dr, dc)
  );
}

/** Wartość komórki przy wypełnieniu przeciągnięciem — formuły z przesuniętymi odwołaniami względnymi. */
function shiftFillCellValue(raw: string, dr: number, dc: number): string {
  if (dr === 0 && dc === 0) return raw;
  const t = raw.trim();
  if (!t.startsWith('=')) return raw;
  const body = t.slice(1).trim();
  return `=${shiftFormulaBody(body, dr, dc)}`;
}

function expandSumaCalls(
  grid: string[][],
  body: string,
  visiting: Set<string>,
  depth: number
): string {
  let s = body;
  for (let i = 0; i < 64; i++) {
    SUMA_CALL_RE.lastIndex = 0;
    const next = s.replace(SUMA_CALL_RE, (_m, colA: string, rowA: string, colB?: string, rowB?: string) => {
      const c1 = colLetterToIndex(colA);
      const r1 = parseInt(rowA, 10) - 1;
      const c2 = colB != null && colB !== '' ? colLetterToIndex(colB) : c1;
      const r2 = rowB != null && rowB !== '' ? parseInt(String(rowB), 10) - 1 : r1;
      if (
        c1 < 0 ||
        c2 < 0 ||
        c1 >= SCRATCH_COLS ||
        c2 >= SCRATCH_COLS ||
        r1 < 0 ||
        r2 < 0 ||
        r1 >= SCRATCH_ROWS ||
        r2 >= SCRATCH_ROWS
      )
        return '(0/0)';
      const sum = sumRectInGrid(grid, r1, c1, r2, c2, visiting, depth);
      if (sum === null) return '(0/0)';
      return `(${sum})`;
    });
    if (next === s) break;
    s = next;
  }
  return s;
}

/** Zamienia odwołania B2, funkcje Suma, potem liczy wyrażenie. */
function evaluateFormulaBody(grid: string[][], body: string, visiting: Set<string>, depth: number): number | null {
  const afterSuma = expandSumaCalls(grid, body, visiting, depth);
  const expanded = afterSuma.replace(CELL_REF_RE, (_m, colLetter: string, rowStr: string) => {
    const c = colLetterToIndex(colLetter);
    const r = parseInt(rowStr, 10) - 1;
    if (c < 0 || c >= SCRATCH_COLS || r < 0 || r >= SCRATCH_ROWS) return '(0/0)';
    const val = getNumericCellValueForFormula(grid, r, c, visiting, depth);
    if (val === null) return '(0/0)';
    return `(${val})`;
  });
  try {
    const n = Function(`"use strict"; return (${expanded})`)();
    return typeof n === 'number' && Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

/**
 * Oblicza formułę z komórki (selfR, selfC) — ta komórka jest w `visiting`, żeby zablokować =C3+C3 przy edycji C3.
 */
function evaluateScratchFormula(grid: string[][], raw: string, selfR: number, selfC: number): number | null {
  const t = raw.trim();
  if (!t.startsWith('=')) return null;
  const visiting = new Set<string>();
  visiting.add(scratchCellKey(selfR, selfC));
  return evaluateFormulaBody(grid, t.slice(1).trim().replace(/,/g, '.'), visiting, 0);
}

function formatCellAfterBlur(grid: string[][], selfR: number, selfC: number, raw: string): string {
  const t = raw.trim();
  if (!t) return '';
  /** Formuły zostają w siatce jako tekst (=…); wynik widać tylko w komórce (poza edycją) i w pasku fx jest formuła. */
  if (t.startsWith('=')) {
    const ev = evaluateScratchFormula(grid, t, selfR, selfC);
    if (ev !== null) return t;
    return raw;
  }
  const n = parseLoosePlDecimal(t);
  if (n !== null) {
    return n.toLocaleString('pl-PL', { minimumFractionDigits: 0, maximumFractionDigits: 6 });
  }
  return raw;
}

/** W komórce (read-only) pokazuj wynik formuły; w edycji i w pasku fx — surowy tekst z siatki. */
function getScratchCellDisplayString(grid: string[][], r: number, c: number): string {
  const raw = grid[r]?.[c] ?? '';
  const t = raw.trim();
  if (!t) return '';
  if (t.startsWith('=')) {
    const ev = evaluateScratchFormula(grid, raw, r, c);
    if (ev !== null) {
      return ev.toLocaleString('pl-PL', { minimumFractionDigits: 0, maximumFractionDigits: 6 });
    }
  }
  return raw;
}

export type PozycjaCalcScratchpadProps = {
  lpLabel: string;
  /** Tekst z kolumny „Podstawa” (np. `KNR 2-01 0233-09`) — do pierwszego wypełnienia arkusza KNR z bazy. */
  podstawaLine?: string;
  /** Roboczogodzina (zł) z Karty Info — kolumna D dla wierszy z typem nakładu `R`. */
  roboczogodzinaZl?: string;
  scratchSheets: CalcScratchBundle;
  onScratchSheetsChange: (next: CalcScratchBundle) => void;
  onClose: () => void;
  onApplyCenaJedn: (formattedPl: string) => void;
};

function isKnrSheetBlank(grid: string[][]): boolean {
  const g = normalizeScratchGridProp(grid);
  for (let r = 0; r < g.length; r++) {
    for (let c = 0; c < SCRATCH_COLS; c++) {
      if (String(g[r][c] ?? '').trim() !== '') return false;
    }
  }
  return true;
}

function formatNormaForScratchCell(raw: string): string {
  const t = raw.trim().replace(/\s/g, '').replace(',', '.');
  const n = Number(t);
  if (!Number.isFinite(n)) return raw.trim();
  return n.toLocaleString('pl-PL', { minimumFractionDigits: 0, maximumFractionDigits: 8 });
}

function buildKnrRmsScratchGrid(
  rows: { naklad_typ: string; naklad_naz: string; norma: string }[]
): string[][] {
  const g = makeEmptyScratchGrid();
  g[0][0] = 'Typ';
  g[0][1] = 'Nazwa';
  g[0][2] = 'Norma';
  g[0][3] = 'Cena jedn.';
  g[0][4] = 'Wartość';

  /** Nagłówek + wiersze RMS + jeden wiersz sumy pod kolumną E */
  const maxData = Math.min(rows.length, SCRATCH_ROWS - 2);
  for (let i = 0; i < maxData; i++) {
    const row = rows[i];
    const rowIdx = i + 1;
    const excelRow = rowIdx + 1;
    g[rowIdx][0] = row.naklad_typ ?? '';
    g[rowIdx][1] = row.naklad_naz ?? '';
    g[rowIdx][2] = formatNormaForScratchCell(row.norma ?? '');
    g[rowIdx][3] = '';
    g[rowIdx][4] = `=C${excelRow}*D${excelRow}`;
  }

  if (maxData > 0) {
    const sumRowIdx = maxData + 1;
    const lastDataExcelRow = maxData + 1;
    g[sumRowIdx][3] = 'Razem';
    g[sumRowIdx][4] = `=Suma(E2:E${lastDataExcelRow})`;
  }
  return g;
}

function formatRoboczogodzinaForKnrDCell(raw: string): string {
  if (!String(raw).trim()) return '';
  const n = parseLoosePlDecimal(String(raw));
  if (n === null) return String(raw).trim();
  return n.toLocaleString('pl-PL', { minimumFractionDigits: 0, maximumFractionDigits: 6 });
}

/** Ustawia kolumnę D (cena jedn.) dla wierszy z typem R — zwraca `true`, gdy zmieniono siatkę.
 * Gdy `priceRaw` jest puste — NIE czyścimy istniejących cen (mogą pochodzić z ATH/ręcznej edycji).
 */
function applyRoboczogodzinaToKnrRowsD(g: string[][], priceRaw: string): boolean {
  if (String(priceRaw ?? '').trim() === '') return false;
  const formatted = formatRoboczogodzinaForKnrDCell(priceRaw);
  let changed = false;
  for (let r = 1; r < SCRATCH_ROWS; r++) {
    const typ = String(g[r]?.[0] ?? '').trim().toUpperCase();
    if (typ !== 'R') continue;
    if ((g[r][3] ?? '') !== formatted) {
      g[r][3] = formatted;
      changed = true;
    }
  }
  return changed;
}

/** Kolor linii siatki jak w Google Sheets */
const GRID_LINE = '#dadce0';
const HEADER_BG = '#f3f3f3';
const CELL_BG = '#ffffff';
const SELECTION_OUTLINE = '#1a73e8';
/** Obwódka całego zakresu (cieńsza niż komórka aktywna) */
const SELECTION_RANGE_EDGE = 'rgba(26, 115, 232, 0.82)';
/** Linie między komórkami w zaznaczeniu (jak wewnętrzna siatka w Arkuszach) */
const SELECTION_INTERNAL_LINE = 'rgba(26, 115, 232, 0.38)';
const RANGE_FILL = 'rgba(26, 115, 232, 0.08)';

/** Podgląd zakresu przy =Suma( — jak pomarańczowe podświetlenie w Arkuszach */
const SUMA_REF_FILL = 'rgba(234, 153, 71, 0.18)';
const SUMA_REF_EDGE = 'rgba(232, 113, 10, 0.95)';
const SUMA_REF_INTERNAL = 'rgba(232, 113, 10, 0.42)';

type CellPos = { r: number; c: number };

function rangeBounds(a: CellPos, b: CellPos): { r0: number; r1: number; c0: number; c1: number } {
  return {
    r0: Math.min(a.r, b.r),
    r1: Math.max(a.r, b.r),
    c0: Math.min(a.c, b.c),
    c1: Math.max(a.c, b.c),
  };
}

/** Tekst ze schowka (Excel / Arkusze Google) → wiersze z komórkami rozdzielonymi tabulatorami. */
function parseClipboardPlainToRows(text: string): string[][] {
  let normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  if (normalized === '') return [['']];
  if (normalized.endsWith('\n')) {
    normalized = normalized.slice(0, -1);
  }
  return normalized.split('\n').map((line) => line.split('\t'));
}

function escapeCellForClipboardTsv(v: string): string {
  return v.replace(/\r\n|\r|\n/g, ' ');
}

function buildTsvFromGridRegion(
  grid: string[][],
  r0: number,
  r1: number,
  c0: number,
  c1: number
): string {
  const lines: string[] = [];
  for (let r = r0; r <= r1; r++) {
    const cells: string[] = [];
    for (let c = c0; c <= c1; c++) {
      cells.push(escapeCellForClipboardTsv(grid[r]?.[c] ?? ''));
    }
    lines.push(cells.join('\t'));
  }
  return lines.join('\n');
}

/** Styl zaznaczenia zbliżony do Google Sheets: gruba ramka komórki startu, cieńsza obwódka zakresu, linie wewnętrzne. */
function sheetsRangeBoxShadow(
  r: number,
  c: number,
  bounds: { r0: number; r1: number; c0: number; c1: number },
  origin: CellPos | null,
  isMulti: boolean
): string {
  const { r0, r1, c0, c1 } = bounds;
  if (!isMulti) {
    return `inset 0 0 0 2px ${SELECTION_OUTLINE}`;
  }
  const shadows: string[] = [];
  const isOrigin = origin != null && origin.r === r && origin.c === c;

  if (isOrigin) {
    shadows.push(`inset 0 0 0 2px ${SELECTION_OUTLINE}`);
    if (r < r1) shadows.push(`inset 0 -1px 0 0 ${SELECTION_INTERNAL_LINE}`);
    if (c < c1) shadows.push(`inset -1px 0 0 0 ${SELECTION_INTERNAL_LINE}`);
  } else {
    if (r === r0) shadows.push(`inset 0 1px 0 0 ${SELECTION_RANGE_EDGE}`);
    if (r === r1) shadows.push(`inset 0 -1px 0 0 ${SELECTION_RANGE_EDGE}`);
    if (c === c0) shadows.push(`inset 1px 0 0 0 ${SELECTION_RANGE_EDGE}`);
    if (c === c1) shadows.push(`inset -1px 0 0 0 ${SELECTION_RANGE_EDGE}`);
    if (r < r1) shadows.push(`inset 0 -1px 0 0 ${SELECTION_INTERNAL_LINE}`);
    if (c < c1) shadows.push(`inset -1px 0 0 0 ${SELECTION_INTERNAL_LINE}`);
  }

  return shadows.length ? shadows.join(', ') : 'none';
}

function sumaPreviewBoxShadow(r: number, c: number, bounds: { r0: number; r1: number; c0: number; c1: number }): string {
  const { r0, r1, c0, c1 } = bounds;
  const shadows: string[] = [];
  if (r === r0) shadows.push(`inset 0 1px 0 0 ${SUMA_REF_EDGE}`);
  if (r === r1) shadows.push(`inset 0 -1px 0 0 ${SUMA_REF_EDGE}`);
  if (c === c0) shadows.push(`inset 1px 0 0 0 ${SUMA_REF_EDGE}`);
  if (c === c1) shadows.push(`inset -1px 0 0 0 ${SUMA_REF_EDGE}`);
  if (r < r1) shadows.push(`inset 0 -1px 0 0 ${SUMA_REF_INTERNAL}`);
  if (c < c1) shadows.push(`inset -1px 0 0 0 ${SUMA_REF_INTERNAL}`);
  return shadows.length ? shadows.join(', ') : 'none';
}

/** Podgląd docelowego zakresu przy przeciąganiu uchwytu wypełnienia (jak niebieski kontur w Arkuszach). */
const FILL_GRAB_EDGE = 'rgba(26, 115, 232, 0.92)';
const FILL_GRAB_INTERNAL = 'rgba(26, 115, 232, 0.42)';

function fillGrabPreviewBoxShadow(r: number, c: number, bounds: { r0: number; r1: number; c0: number; c1: number }): string {
  const { r0, r1, c0, c1 } = bounds;
  const shadows: string[] = [];
  if (r === r0) shadows.push(`inset 0 1px 0 0 ${FILL_GRAB_EDGE}`);
  if (r === r1) shadows.push(`inset 0 -1px 0 0 ${FILL_GRAB_EDGE}`);
  if (c === c0) shadows.push(`inset 1px 0 0 0 ${FILL_GRAB_EDGE}`);
  if (c === c1) shadows.push(`inset -1px 0 0 0 ${FILL_GRAB_EDGE}`);
  if (r < r1) shadows.push(`inset 0 -1px 0 0 ${FILL_GRAB_INTERNAL}`);
  if (c < c1) shadows.push(`inset -1px 0 0 0 ${FILL_GRAB_INTERNAL}`);
  return shadows.length ? shadows.join(', ') : 'none';
}

const DEFAULT_INPUT_COLOR = '#202124';

const baseSheetCellInputSx: React.CSSProperties = {
  display: 'block',
  width: '100%',
  minHeight: 26,
  boxSizing: 'border-box',
  margin: 0,
  padding: '3px 6px',
  border: 'none',
  borderRadius: 0,
  outline: 'none',
  boxShadow: 'none',
  fontFamily: 'Roboto, Arial, sans-serif',
  fontSize: 13,
  lineHeight: 1.25,
  color: DEFAULT_INPUT_COLOR,
  backgroundColor: 'transparent',
};

function mergeInputStyle(cellStyle: ScratchCellStyle, editing: boolean): React.CSSProperties {
  return {
    fontWeight: cellStyle.bold ? 700 : 400,
    fontStyle: cellStyle.italic ? 'italic' : 'normal',
    textDecoration: cellStyle.underline ? 'underline' : 'none',
    color: cellStyle.color || DEFAULT_INPUT_COLOR,
    ...(editing ? {} : { caretColor: 'transparent' }),
  };
}

/**
 * Mini arkusz 5×50 — formuły, Suma(zakres), formatowanie, zaznaczenie zakresu przeciągnięciem myszy (jak w Excelu).
 */
const PozycjaCalcScratchpad: React.FC<PozycjaCalcScratchpadProps> = ({
  lpLabel,
  podstawaLine = '',
  roboczogodzinaZl = '',
  scratchSheets,
  onScratchSheetsChange,
  onClose,
  onApplyCenaJedn,
}) => {
  const [activeSheet, setActiveSheet] = React.useState<ScratchSheetId>('wlasne');

  const grid = scratchSheets[activeSheet].grid;
  const cellStyles = scratchSheets[activeSheet].cellStyles;

  const patchActiveGrid = React.useCallback(
    (nextGrid: string[][]) => {
      onScratchSheetsChange({
        ...scratchSheets,
        [activeSheet]: { ...scratchSheets[activeSheet], grid: nextGrid },
      });
    },
    [scratchSheets, activeSheet, onScratchSheetsChange]
  );

  const patchActiveCellStyles = React.useCallback(
    (nextStyles: ScratchCellStyle[][]) => {
      onScratchSheetsChange({
        ...scratchSheets,
        [activeSheet]: { ...scratchSheets[activeSheet], cellStyles: nextStyles },
      });
    },
    [scratchSheets, activeSheet, onScratchSheetsChange]
  );

  const applyActiveScratchSheet = React.useCallback(
    (nextGrid: string[][], nextStyles: ScratchCellStyle[][]) => {
      onScratchSheetsChange({
        ...scratchSheets,
        [activeSheet]: { grid: nextGrid, cellStyles: nextStyles },
      });
    },
    [scratchSheets, activeSheet, onScratchSheetsChange]
  );

  const normalizedGrid = React.useMemo(() => normalizeScratchGridProp(grid), [grid]);
  const gridDimsOk = scratchGridDimensionsOk(grid);
  React.useEffect(() => {
    if (!gridDimsOk) patchActiveGrid(normalizedGrid);
  }, [grid, gridDimsOk, normalizedGrid, patchActiveGrid]);

  const normalizedCellStyles = React.useMemo(() => normalizeScratchCellStylesProp(cellStyles), [cellStyles]);
  const cellStylesDimsOk = scratchCellStylesDimensionsOk(cellStyles);
  React.useEffect(() => {
    if (!cellStylesDimsOk) patchActiveCellStyles(normalizedCellStyles);
  }, [cellStyles, cellStylesDimsOk, normalizedCellStyles, patchActiveCellStyles]);

  const normalizedGridRef = React.useRef(normalizedGrid);
  normalizedGridRef.current = normalizedGrid;
  const normalizedCellStylesRef = React.useRef(normalizedCellStyles);
  normalizedCellStylesRef.current = normalizedCellStyles;

  const [selected, setSelected] = React.useState<{ r: number; c: number } | null>(null);
  const [rangeAnchor, setRangeAnchor] = React.useState<{ r: number; c: number } | null>(null);
  const [rangeEnd, setRangeEnd] = React.useState<{ r: number; c: number } | null>(null);
  /** Komórka, od której zaczęło się zaznaczenie (grubsza ramka jak pierwsza w Arkuszach). */
  const [selectionOrigin, setSelectionOrigin] = React.useState<{ r: number; c: number } | null>(null);
  const rangeAnchorRef = React.useRef<{ r: number; c: number } | null>(null);
  /** Przeciąganie zaznaczenia: anchor → lastEnd; sumaMode = podgląd zakresu dla =Suma( */
  const dragSelectRef = React.useRef<{
    active: boolean;
    anchor: { r: number; c: number };
    dragged: boolean;
    lastEnd: { r: number; c: number };
    sumaMode: boolean;
  } | null>(null);
  const [isRangeDragging, setIsRangeDragging] = React.useState(false);
  /** Prostokąt pomarańczowy przy wyborze zakresu do Suma( */
  const [sumaPreviewBounds, setSumaPreviewBounds] = React.useState<{
    r0: number;
    r1: number;
    c0: number;
    c1: number;
  } | null>(null);
  /** Podgląd prostokąta docelowego przy przeciąganiu uchwytu wypełnienia. */
  const [fillPreviewBounds, setFillPreviewBounds] = React.useState<{
    r0: number;
    r1: number;
    c0: number;
    c1: number;
  } | null>(null);
  /** null = tylko zaznaczenie (jak Arkusze); ustawione = aktywna edycja z kursorem w inputcie */
  const [editingCell, setEditingCell] = React.useState<{ r: number; c: number } | null>(null);

  const fillDragRef = React.useRef<{
    bounds: { r0: number; r1: number; c0: number; c1: number };
    srcGrid: string[][];
    srcStyles: ScratchCellStyle[][];
  } | null>(null);
  const fillDestRef = React.useRef<{ r: number; c: number } | null>(null);

  React.useEffect(() => {
    setActiveSheet('wlasne');
    setSelected(null);
    setRangeAnchor(null);
    setRangeEnd(null);
    setSelectionOrigin(null);
    setEditingCell(null);
    setSumaPreviewBounds(null);
    setFillPreviewBounds(null);
    fillDragRef.current = null;
    fillDestRef.current = null;
    dragSelectRef.current = null;
    setIsRangeDragging(false);
  }, [lpLabel]);

  const scratchSheetsRef = React.useRef(scratchSheets);
  scratchSheetsRef.current = scratchSheets;
  const roboczogodzinaZlRef = React.useRef(roboczogodzinaZl);
  roboczogodzinaZlRef.current = roboczogodzinaZl;
  /**
   * Nakłady z KNR_rms: jedno pobranie z API na pozycję (lpLabel), przy pierwszym udanym wejściu na zakładkę KNR.
   * Kolejne przełączenia Własne ↔ KNR nie wywołują ponownie żądania — ponowne pobranie: przycisk „Odśwież KNR” na pasku.
   */
  const knrRmsFetchedOnceRef = React.useRef(false);
  /** Inkrementacja wymusza ponowne uruchomienie efektu pobierania po ręcznym wyczyszczeniu arkusza KNR. */
  const [knrReloadNonce, setKnrReloadNonce] = React.useState(0);

  React.useEffect(() => {
    knrRmsFetchedOnceRef.current = false;
  }, [lpLabel]);

  const reloadKnrSheetFromCatalog = React.useCallback(() => {
    if (activeSheet !== 'knr') return;
    const line = podstawaLine.trim();
    if (!line || !parseKnrAndSymbolFromPodstawa(line)) return;
    knrRmsFetchedOnceRef.current = false;
    onScratchSheetsChange({
      ...scratchSheetsRef.current,
      knr: { grid: makeEmptyScratchGrid(), cellStyles: makeEmptyScratchStyles() },
    });
    setKnrReloadNonce((n) => n + 1);
  }, [activeSheet, podstawaLine, onScratchSheetsChange]);

  React.useEffect(() => {
    if (activeSheet !== 'knr') return;
    if (knrRmsFetchedOnceRef.current) return;

    const knrGrid = scratchSheetsRef.current.knr.grid;
    if (!isKnrSheetBlank(knrGrid)) {
      knrRmsFetchedOnceRef.current = true;
      return;
    }

    const line = podstawaLine.trim();
    if (!line) return;

    const parsed = parseKnrAndSymbolFromPodstawa(line);
    if (!parsed) return;

    let cancelled = false;
    (async () => {
      try {
        const rows = await fetchKnrRms(parsed.knr, parsed.symbol);
        if (cancelled) return;
        knrRmsFetchedOnceRef.current = true;
        const grid = buildKnrRmsScratchGrid(rows);
        applyRoboczogodzinaToKnrRowsD(grid, roboczogodzinaZlRef.current);
        onScratchSheetsChange({
          ...scratchSheetsRef.current,
          knr: { grid, cellStyles: makeEmptyScratchStyles() },
        });
      } catch {
        /* brak ustawienia fetchedOnce — przy błędzie kolejne wejście na KNR ponowi próbę */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeSheet, lpLabel, podstawaLine, onScratchSheetsChange, knrReloadNonce]);

  React.useEffect(() => {
    const bundle = scratchSheetsRef.current;
    const g = bundle.knr.grid.map((row) => [...row]);
    if (!applyRoboczogodzinaToKnrRowsD(g, roboczogodzinaZl)) return;
    onScratchSheetsChange({
      ...bundle,
      knr: { ...bundle.knr, grid: g },
    });
  }, [roboczogodzinaZl, onScratchSheetsChange]);

  const sheetRef = React.useRef<HTMLDivElement>(null);
  const selectionSnapRef = React.useRef<{
    anchor: { r: number; c: number } | null;
    end: { r: number; c: number } | null;
    selected: { r: number; c: number } | null;
  }>({ anchor: null, end: null, selected: null });
  selectionSnapRef.current = { anchor: rangeAnchor, end: rangeEnd, selected };

  const [panelWidth, setPanelWidth] = React.useState(loadPanelWidth);
  const [colWidths, setColWidths] = React.useState(loadColWidths);

  const colDragRef = React.useRef<{ i: number; startX: number; left0: number; right0: number } | null>(null);
  const panelDragRef = React.useRef<{ startX: number; startW: number } | null>(null);
  const cellInputRefs = React.useRef<Map<string, HTMLInputElement>>(new Map());
  const formulaBarInputRef = React.useRef<HTMLInputElement | null>(null);

  const setInputRef = React.useCallback((r: number, c: number, el: HTMLInputElement | null) => {
    const k = scratchCellKey(r, c);
    if (el) cellInputRefs.current.set(k, el);
    else cellInputRefs.current.delete(k);
  }, []);

  const isInRangeRect = React.useCallback((r: number, c: number): boolean => {
    if (!rangeAnchor || !rangeEnd) return false;
    const { r0, r1, c0, c1 } = rangeBounds(rangeAnchor, rangeEnd);
    return r >= r0 && r <= r1 && c >= c0 && c <= c1;
  }, [rangeAnchor, rangeEnd]);

  const selectionBounds = React.useMemo(() => {
    if (!rangeAnchor || !rangeEnd) return null;
    return rangeBounds(rangeAnchor, rangeEnd);
  }, [rangeAnchor, rangeEnd]);

  const setCell = React.useCallback(
    (r: number, c: number, v: string) => {
      const next = normalizedGrid.map((row) => [...row]);
      if (!next[r]) return;
      next[r] = [...next[r]];
      next[r][c] = v;
      patchActiveGrid(next);
    },
    [normalizedGrid, patchActiveGrid]
  );

  const readCellUnderPoint = React.useCallback((clientX: number, clientY: number): { r: number; c: number } | null => {
    const list =
      typeof document.elementsFromPoint === 'function'
        ? document.elementsFromPoint(clientX, clientY)
        : [document.elementFromPoint(clientX, clientY)].filter(Boolean);
    for (const node of list) {
      let t: Element | null = node;
      while (t) {
        if (t instanceof HTMLElement && t.dataset.scratchR !== undefined && t.dataset.scratchC !== undefined) {
          const r = parseInt(t.dataset.scratchR, 10);
          const c = parseInt(t.dataset.scratchC, 10);
          if (Number.isFinite(r) && Number.isFinite(c) && r >= 0 && r < SCRATCH_ROWS && c >= 0 && c < SCRATCH_COLS) {
            return { r, c };
          }
          return null;
        }
        t = t.parentElement;
      }
    }
    return null;
  }, []);

  const handleFillHandleMouseDown = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (editingCell || sumaPreviewBounds != null) return;
      const b = selectionBounds;
      if (!b) return;

      const { r0, r1, c0, c1: c1b } = b;
      const srcH = r1 - r0 + 1;
      const srcW = c1b - c0 + 1;
      const srcGrid: string[][] = [];
      const srcStyles: ScratchCellStyle[][] = [];
      for (let ri = 0; ri < srcH; ri++) {
        const gr: string[] = [];
        const sr: ScratchCellStyle[] = [];
        for (let ci = 0; ci < srcW; ci++) {
          gr.push(normalizedGrid[r0 + ri]?.[c0 + ci] ?? '');
          sr.push({ ...(normalizedCellStyles[r0 + ri]?.[c0 + ci] ?? {}) });
        }
        srcGrid.push(gr);
        srcStyles.push(sr);
      }

      fillDragRef.current = { bounds: b, srcGrid, srcStyles };
      fillDestRef.current = { r: r1, c: c1b };
      setFillPreviewBounds({ r0, r1, c0, c1: c1b });

      const onMove = (ev: MouseEvent) => {
        if (!fillDragRef.current) return;
        const hit = readCellUnderPoint(ev.clientX, ev.clientY);
        const fb = fillDragRef.current.bounds;
        if (!hit) return;
        const dR = Math.max(fb.r1, Math.min(SCRATCH_ROWS - 1, hit.r));
        const dC = Math.max(fb.c1, Math.min(SCRATCH_COLS - 1, hit.c));
        fillDestRef.current = { r: dR, c: dC };
        setFillPreviewBounds({ r0: fb.r0, r1: dR, c0: fb.c0, c1: dC });
      };

      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        document.body.style.removeProperty('cursor');
        document.body.style.removeProperty('user-select');

        const fd = fillDragRef.current;
        const dest = fillDestRef.current;
        fillDragRef.current = null;
        fillDestRef.current = null;
        setFillPreviewBounds(null);
        if (!fd || !dest) return;

        const { r0, r1: r1b, c0, c1: c1b } = fd.bounds;
        const dR = dest.r;
        const dC = dest.c;
        const sh = r1b - r0 + 1;
        const sw = c1b - c0 + 1;

        const baseGrid = normalizedGridRef.current.map((row) => [...row]);
        const baseStyles = normalizedCellStylesRef.current.map((row) => row.map((cell) => ({ ...cell })));
        for (let r = r0; r <= dR; r++) {
          for (let c = c0; c <= dC; c++) {
            const sr = r0 + ((r - r0) % sh);
            const sc = c0 + ((c - c0) % sw);
            const dr = r - sr;
            const dc = c - sc;
            const srcVal = fd.srcGrid[sr - r0][sc - c0];
            baseGrid[r][c] = shiftFillCellValue(srcVal, dr, dc);
            baseStyles[r][c] = { ...fd.srcStyles[sr - r0][sc - c0] };
          }
        }
        applyActiveScratchSheet(baseGrid, baseStyles);

        setRangeAnchor({ r: r0, c: c0 });
        setRangeEnd({ r: dR, c: dC });
        setSelectionOrigin({ r: r0, c: c0 });
        setSelected({ r: dR, c: dC });
        rangeAnchorRef.current = { r: r0, c: c0 };
        queueMicrotask(() => sheetRef.current?.focus({ preventScroll: true }));
      };

      document.body.style.cursor = 'crosshair';
      document.body.style.userSelect = 'none';
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [
      editingCell,
      sumaPreviewBounds,
      selectionBounds,
      normalizedGrid,
      normalizedCellStyles,
      readCellUnderPoint,
      applyActiveScratchSheet,
    ]
  );

  const handleInputMouseDown = React.useCallback(
    (r: number, c: number, e: React.MouseEvent) => {
      if (e.button !== 0) return;
      if (editingCell && editingCell.r === r && editingCell.c === c) {
        return;
      }
      if (!editingCell || editingCell.r !== r || editingCell.c !== c) {
        e.preventDefault();
      }
      const ctxEarly = getActiveScratchFormulaInput(
        formulaBarInputRef.current,
        cellInputRefs.current,
        selected
      );
      let sumaMode = false;
      if (ctxEarly) {
        const cur = ctxEarly.el.selectionStart ?? ctxEarly.el.value.length;
        sumaMode = isSumaRangePickingBeforeCursor(ctxEarly.el.value, cur);
      }
      const p = { r, c };
      rangeAnchorRef.current = p;
      setRangeAnchor(p);
      setRangeEnd(p);
      setSelected(p);
      setSelectionOrigin(p);
      dragSelectRef.current = { active: true, anchor: p, dragged: false, lastEnd: p, sumaMode };
      if (sumaMode) {
        setSumaPreviewBounds(rangeBounds(p, p));
      } else {
        setSumaPreviewBounds(null);
      }
    },
    [editingCell, selected]
  );

  React.useEffect(() => {
    const cursorAfterSumaRange = (patched: string, rangeStr: string): number => {
      const openRe = /=\s*Suma\s*\(\s*/gi;
      let lastOpenEnd = -1;
      let m: RegExpExecArray | null;
      while ((m = openRe.exec(patched)) !== null) {
        lastOpenEnd = m.index + m[0].length;
      }
      if (lastOpenEnd < 0) return patched.length;
      return lastOpenEnd + rangeStr.length;
    };

    const cursorAfterClosingParen = (patched: string): number => {
      const openRe = /=\s*Suma\s*\(\s*/gi;
      let lastOpenEnd = -1;
      let m: RegExpExecArray | null;
      while ((m = openRe.exec(patched)) !== null) {
        lastOpenEnd = m.index + m[0].length;
      }
      if (lastOpenEnd < 0) return patched.length;
      const rest = patched.slice(lastOpenEnd);
      const ci = rest.indexOf(')');
      return ci >= 0 ? lastOpenEnd + ci + 1 : patched.length;
    };

    const onMove = (e: MouseEvent) => {
      const d = dragSelectRef.current;
      if (!d?.active || !(e.buttons & 1)) return;
      const hit = readCellUnderPoint(e.clientX, e.clientY);
      if (!hit) return;
      if (!d.dragged && (hit.r !== d.anchor.r || hit.c !== d.anchor.c)) {
        d.dragged = true;
        setIsRangeDragging(true);
      }
      d.lastEnd = hit;
      setRangeEnd(hit);
      setRangeAnchor(d.anchor);
      rangeAnchorRef.current = d.anchor;
      setSelected(hit);

      if (d.sumaMode) {
        setSumaPreviewBounds(rangeBounds(d.anchor, hit));
        const ctx = getActiveScratchFormulaInput(
          formulaBarInputRef.current,
          cellInputRefs.current,
          selected
        );
        if (ctx) {
          const val = ctx.el.value;
          const cur = ctx.el.selectionStart ?? val.length;
          if (isSumaRangePickingBeforeCursor(val, cur)) {
            const rangeStr = formatRangeA1(d.anchor, hit);
            const patched = patchSumaRangeInFormula(val, cur, rangeStr, false);
            if (patched != null) {
              setCell(ctx.cellR, ctx.cellC, patched);
              const ncur = cursorAfterSumaRange(patched, rangeStr);
              queueMicrotask(() => {
                ctx.el.focus({ preventScroll: true });
                try {
                  ctx.el.setSelectionRange(ncur, ncur);
                } catch {
                  /* ignore */
                }
              });
            }
          }
        }
      }
    };
    const onUp = () => {
      const d = dragSelectRef.current;
      if (!d?.active) return;
      const suma = d.sumaMode;
      dragSelectRef.current = null;
      setIsRangeDragging(false);
      setSumaPreviewBounds(null);
      if (suma) {
        const ctx = getActiveScratchFormulaInput(
          formulaBarInputRef.current,
          cellInputRefs.current,
          selected
        );
        if (ctx) {
          const val = ctx.el.value;
          const cur = ctx.el.selectionStart ?? val.length;
          if (isSumaRangePickingBeforeCursor(val, cur)) {
            const rangeStr = formatRangeA1(d.anchor, d.lastEnd);
            const patched = patchSumaRangeInFormula(val, cur, rangeStr, true);
            if (patched != null) {
              setCell(ctx.cellR, ctx.cellC, patched);
              const ncur = cursorAfterClosingParen(patched);
              queueMicrotask(() => {
                ctx.el.focus({ preventScroll: true });
                try {
                  ctx.el.setSelectionRange(ncur, ncur);
                } catch {
                  /* ignore */
                }
              });
            }
          }
        }
        return;
      }
      queueMicrotask(() => {
        sheetRef.current?.focus({ preventScroll: true });
      });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [readCellUnderPoint, setCell, selected]);

  const focusSheetSoon = React.useCallback(() => {
    queueMicrotask(() => {
      sheetRef.current?.focus({ preventScroll: true });
    });
  }, []);

  const moveSelectionTo = React.useCallback((r: number, c: number) => {
    if (r < 0 || r >= SCRATCH_ROWS || c < 0 || c >= SCRATCH_COLS) return;
    setEditingCell(null);
    const p = { r, c };
    rangeAnchorRef.current = p;
    setRangeAnchor(p);
    setRangeEnd(p);
    setSelected(p);
    setSelectionOrigin(p);
  }, []);

  const beginReplaceCellWithChar = React.useCallback(
    (r: number, c: number, ch: string) => {
      if (r < 0 || r >= SCRATCH_ROWS || c < 0 || c >= SCRATCH_COLS) return;
      const next = normalizedGrid.map((row) => [...row]);
      if (!next[r]) return;
      next[r] = [...next[r]];
      next[r][c] = ch;
      patchActiveGrid(next);
      const p = { r, c };
      rangeAnchorRef.current = p;
      setRangeAnchor(p);
      setRangeEnd(p);
      setSelected(p);
      setSelectionOrigin(p);
      setEditingCell(p);
      const len = ch.length;
      queueMicrotask(() => {
        const el = cellInputRefs.current.get(scratchCellKey(r, c));
        if (!el) return;
        el.focus({ preventScroll: true });
        try {
          el.setSelectionRange(len, len);
        } catch {
          /* ignore */
        }
      });
    },
    [normalizedGrid, patchActiveGrid]
  );

  const enterEditMode = React.useCallback((r: number, c: number, caret: 'start' | 'end' | 'all' = 'end') => {
    if (r < 0 || r >= SCRATCH_ROWS || c < 0 || c >= SCRATCH_COLS) return;
    const p = { r, c };
    rangeAnchorRef.current = p;
    setRangeAnchor(p);
    setRangeEnd(p);
    setSelected(p);
    setSelectionOrigin(p);
    setEditingCell(p);
    queueMicrotask(() => {
      const el = cellInputRefs.current.get(scratchCellKey(r, c));
      if (!el) return;
      el.focus({ preventScroll: true });
      const L = el.value.length;
      try {
        if (caret === 'start') el.setSelectionRange(0, 0);
        else if (caret === 'all') el.setSelectionRange(0, L);
        else el.setSelectionRange(L, L);
      } catch {
        /* ignore */
      }
    });
  }, []);

  const clearRectInGrid = React.useCallback(
    (r0: number, r1: number, c0: number, c1: number) => {
      const next = normalizedGrid.map((row) => [...row]);
      for (let r = r0; r <= r1; r++) {
        for (let c = c0; c <= c1; c++) {
          if (next[r]) next[r][c] = '';
        }
      }
      patchActiveGrid(next);
    },
    [normalizedGrid, patchActiveGrid]
  );

  const isScratchClipboardNativeTarget = React.useCallback((target: EventTarget | null): boolean => {
    if (!(target instanceof HTMLElement)) return false;
    if (target instanceof HTMLInputElement && target.dataset.scratchR !== undefined) return true;
    const fb = formulaBarInputRef.current;
    if (fb && (target === fb || fb.contains(target))) return true;
    return false;
  }, []);

  const handleScratchCopy = React.useCallback(
    (e: React.ClipboardEvent) => {
      if (editingCell) return;
      if (isScratchClipboardNativeTarget(e.target)) return;
      const b = selectionBounds;
      if (!b || !rangeAnchor || !rangeEnd) return;
      e.preventDefault();
      e.clipboardData.setData('text/plain', buildTsvFromGridRegion(normalizedGrid, b.r0, b.r1, b.c0, b.c1));
    },
    [editingCell, isScratchClipboardNativeTarget, selectionBounds, rangeAnchor, rangeEnd, normalizedGrid]
  );

  const handleScratchCut = React.useCallback(
    (e: React.ClipboardEvent) => {
      if (editingCell) return;
      if (isScratchClipboardNativeTarget(e.target)) return;
      const b = selectionBounds;
      if (!b || !rangeAnchor || !rangeEnd) return;
      e.preventDefault();
      e.clipboardData.setData('text/plain', buildTsvFromGridRegion(normalizedGrid, b.r0, b.r1, b.c0, b.c1));
      clearRectInGrid(b.r0, b.r1, b.c0, b.c1);
    },
    [editingCell, isScratchClipboardNativeTarget, selectionBounds, rangeAnchor, rangeEnd, normalizedGrid, clearRectInGrid]
  );

  const handleScratchPaste = React.useCallback(
    (e: React.ClipboardEvent) => {
      if (editingCell) return;
      if (isScratchClipboardNativeTarget(e.target)) return;
      if (!rangeAnchor || !rangeEnd) return;
      const text = e.clipboardData.getData('text/plain');
      if (text === '') return;
      const pasteAnchor = rangeBounds(rangeAnchor, rangeEnd);
      e.preventDefault();
      const parsedRows = parseClipboardPlainToRows(text);
      const next = normalizedGrid.map((row) => [...row]);
      for (let i = 0; i < parsedRows.length; i++) {
        const r = pasteAnchor.r0 + i;
        if (r >= SCRATCH_ROWS) break;
        const line = parsedRows[i];
        for (let j = 0; j < line.length; j++) {
          const c = pasteAnchor.c0 + j;
          if (c >= SCRATCH_COLS) break;
          next[r][c] = line[j];
        }
      }
      patchActiveGrid(next);
    },
    [editingCell, isScratchClipboardNativeTarget, rangeAnchor, rangeEnd, normalizedGrid, patchActiveGrid]
  );

  const handleSheetKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.nativeEvent.isComposing) return;
      if (editingCell) return;

      const { anchor, end, selected: sel } = selectionSnapRef.current;

      const ensureSelection = (): { r: number; c: number } => {
        if (anchor && end && sel) return sel;
        moveSelectionTo(0, 0);
        return { r: 0, c: 0 };
      };

      if (e.key === 'Tab') {
        e.preventDefault();
        const s = ensureSelection();
        if (e.shiftKey) {
          if (s.c > 0) moveSelectionTo(s.r, s.c - 1);
          else if (s.r > 0) moveSelectionTo(s.r - 1, SCRATCH_COLS - 1);
        } else {
          if (s.c < SCRATCH_COLS - 1) moveSelectionTo(s.r, s.c + 1);
          else if (s.r < SCRATCH_ROWS - 1) moveSelectionTo(s.r + 1, 0);
        }
        return;
      }

      if (e.key === 'Enter') {
        e.preventDefault();
        const s = ensureSelection();
        enterEditMode(s.r, s.c, 'end');
        return;
      }

      if (e.key === 'F2') {
        e.preventDefault();
        const s = ensureSelection();
        enterEditMode(s.r, s.c, 'end');
        return;
      }

      if (e.key === 'Escape') {
        e.preventDefault();
        return;
      }

      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        const a = anchor && end ? anchor : null;
        const b = anchor && end ? end : null;
        if (!a || !b) {
          const s = ensureSelection();
          clearRectInGrid(s.r, s.r, s.c, s.c);
          return;
        }
        const r0 = Math.min(a.r, b.r);
        const r1 = Math.max(a.r, b.r);
        const c0 = Math.min(a.c, b.c);
        const c1 = Math.max(a.c, b.c);
        clearRectInGrid(r0, r1, c0, c1);
        return;
      }

      const isPrintable =
        e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey && e.key !== '\u0000';

      if (isPrintable) {
        e.preventDefault();
        const s = ensureSelection();
        beginReplaceCellWithChar(s.r, s.c, e.key);
        return;
      }

      if (!anchor || !end || !sel) {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          e.preventDefault();
          moveSelectionTo(0, 0);
        }
        return;
      }

      const r0 = Math.min(anchor.r, end.r);
      const r1 = Math.max(anchor.r, end.r);
      const c0 = Math.min(anchor.c, end.c);
      const c1 = Math.max(anchor.c, end.c);

      if (e.shiftKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
        e.preventDefault();
        if (e.key === 'ArrowDown' && r1 < SCRATCH_ROWS - 1) {
          const na = { r: r0, c: c0 };
          const ne = { r: r1 + 1, c: c1 };
          rangeAnchorRef.current = na;
          setRangeAnchor(na);
          setRangeEnd(ne);
          setSelected(ne);
        } else if (e.key === 'ArrowUp' && r0 > 0) {
          const na = { r: r0 - 1, c: c0 };
          const ne = { r: r1, c: c1 };
          rangeAnchorRef.current = na;
          setRangeAnchor(na);
          setRangeEnd(ne);
          setSelected(na);
        } else if (e.key === 'ArrowRight' && c1 < SCRATCH_COLS - 1) {
          const na = { r: r0, c: c0 };
          const ne = { r: r1, c: c1 + 1 };
          rangeAnchorRef.current = na;
          setRangeAnchor(na);
          setRangeEnd(ne);
          setSelected(ne);
        } else if (e.key === 'ArrowLeft' && c0 > 0) {
          const na = { r: r0, c: c0 - 1 };
          const ne = { r: r1, c: c1 };
          rangeAnchorRef.current = na;
          setRangeAnchor(na);
          setRangeEnd(ne);
          setSelected(na);
        }
        return;
      }

      if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        let nr = sel.r;
        let nc = sel.c;
        if (e.key === 'ArrowUp') nr = Math.max(0, sel.r - 1);
        else if (e.key === 'ArrowDown') nr = Math.min(SCRATCH_ROWS - 1, sel.r + 1);
        else if (e.key === 'ArrowLeft') nc = Math.max(0, sel.c - 1);
        else if (e.key === 'ArrowRight') nc = Math.min(SCRATCH_COLS - 1, sel.c + 1);
        moveSelectionTo(nr, nc);
      }
    },
    [
      editingCell,
      moveSelectionTo,
      enterEditMode,
      beginReplaceCellWithChar,
      clearRectInGrid,
    ]
  );

  const handleScratchCellKeyDown = React.useCallback(
    (r: number, c: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.nativeEvent.isComposing) return;
      const input = e.currentTarget;
      const { value } = input;
      const selStart = input.selectionStart ?? 0;
      const selEnd = input.selectionEnd ?? 0;

      const leaveCellTo = (nr: number, nc: number) => {
        e.preventDefault();
        input.blur();
        setEditingCell(null);
        queueMicrotask(() => {
          moveSelectionTo(nr, nc);
          focusSheetSoon();
        });
      };

      if (e.key === 'ArrowUp') {
        if (r > 0 && selStart === 0 && selEnd === 0) {
          leaveCellTo(r - 1, c);
        }
        return;
      }
      if (e.key === 'ArrowDown') {
        if (r < SCRATCH_ROWS - 1 && selStart === value.length && selEnd === value.length) {
          leaveCellTo(r + 1, c);
        }
        return;
      }
      if (e.key === 'ArrowLeft' && selStart === 0 && selEnd === 0) {
        if (c > 0) leaveCellTo(r, c - 1);
        return;
      }
      if (e.key === 'ArrowRight' && selStart === value.length && selEnd === value.length) {
        if (c < SCRATCH_COLS - 1) leaveCellTo(r, c + 1);
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        input.blur();
        setEditingCell(null);
        queueMicrotask(() => {
          if (e.shiftKey) {
            if (r > 0) moveSelectionTo(r - 1, c);
            else moveSelectionTo(r, c);
          } else if (r < SCRATCH_ROWS - 1) {
            moveSelectionTo(r + 1, c);
          } else {
            moveSelectionTo(r, c);
          }
          focusSheetSoon();
        });
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        input.blur();
        setEditingCell(null);
        focusSheetSoon();
        return;
      }
      if (e.key === 'Tab') {
        e.preventDefault();
        input.blur();
        setEditingCell(null);
        queueMicrotask(() => {
          if (e.shiftKey) {
            if (c > 0) moveSelectionTo(r, c - 1);
            else if (r > 0) moveSelectionTo(r - 1, SCRATCH_COLS - 1);
          } else {
            if (c < SCRATCH_COLS - 1) moveSelectionTo(r, c + 1);
            else if (r < SCRATCH_ROWS - 1) moveSelectionTo(r + 1, 0);
          }
          focusSheetSoon();
        });
      }
    },
    [moveSelectionTo, focusSheetSoon]
  );

  React.useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const cd = colDragRef.current;
      if (cd) {
        const delta = e.clientX - cd.startX;
        const total = cd.left0 + cd.right0;
        const newLeft = Math.round(
          Math.min(total - MIN_DATA_COL_PX, Math.max(MIN_DATA_COL_PX, cd.left0 + delta))
        );
        const newRight = total - newLeft;
        setColWidths((prev) => {
          const next = [...prev];
          next[cd.i] = newLeft;
          next[cd.i + 1] = newRight;
          return next;
        });
      }
      const pd = panelDragRef.current;
      if (pd) {
        const delta = pd.startX - e.clientX;
        const next = Math.min(PANEL_WIDTH_MAX, Math.max(PANEL_WIDTH_MIN, Math.round(pd.startW + delta)));
        setPanelWidth(next);
      }
    };
    const onUp = () => {
      if (colDragRef.current) {
        colDragRef.current = null;
        setColWidths((prev) => {
          try {
            localStorage.setItem(LS_COL_WIDTHS, JSON.stringify(prev));
          } catch {
            /* ignore */
          }
          return prev;
        });
      }
      if (panelDragRef.current) {
        panelDragRef.current = null;
        setPanelWidth((w) => {
          try {
            localStorage.setItem(LS_PANEL_WIDTH, String(w));
          } catch {
            /* ignore */
          }
          return w;
        });
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  const startColResize = (colIndex: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    colDragRef.current = {
      i: colIndex,
      startX: e.clientX,
      left0: colWidths[colIndex],
      right0: colWidths[colIndex + 1],
    };
  };

  const startPanelResize = (e: React.MouseEvent) => {
    e.preventDefault();
    panelDragRef.current = { startX: e.clientX, startW: panelWidth };
  };

  const updateStylesForSelection = React.useCallback(
    (mutate: (s: ScratchCellStyle) => ScratchCellStyle) => {
      const anchor = rangeAnchor ?? selected;
      const end = rangeEnd ?? anchor;
      if (!anchor || !end) return;
      const next = normalizedCellStyles.map((row) => row.map((cell) => ({ ...cell })));
      iterRect(anchor, end, (r, c) => {
        next[r][c] = mutate({ ...next[r][c] });
      });
      patchActiveCellStyles(next);
    },
    [normalizedCellStyles, patchActiveCellStyles, rangeAnchor, rangeEnd, selected]
  );

  const toggleBold = React.useCallback(() => {
    updateStylesForSelection((s) => ({ ...s, bold: !s.bold }));
  }, [updateStylesForSelection]);

  const toggleItalic = React.useCallback(() => {
    updateStylesForSelection((s) => ({ ...s, italic: !s.italic }));
  }, [updateStylesForSelection]);

  const toggleUnderline = React.useCallback(() => {
    updateStylesForSelection((s) => ({ ...s, underline: !s.underline }));
  }, [updateStylesForSelection]);

  const setBgColor = React.useCallback(
    (hex: string) => {
      updateStylesForSelection((s) => ({ ...s, backgroundColor: hex }));
    },
    [updateStylesForSelection]
  );

  const setFgColor = React.useCallback(
    (hex: string) => {
      updateStylesForSelection((s) => ({ ...s, color: hex }));
    },
    [updateStylesForSelection]
  );

  const clearBgColor = React.useCallback(() => {
    updateStylesForSelection((s) => {
      const next = { ...s };
      delete next.backgroundColor;
      return next;
    });
  }, [updateStylesForSelection]);

  const clearFgColor = React.useCallback(() => {
    updateStylesForSelection((s) => {
      const next = { ...s };
      delete next.color;
      return next;
    });
  }, [updateStylesForSelection]);

  const [scratchPalette, setScratchPalette] = React.useState<{
    anchor: HTMLElement;
    mode: 'bg' | 'fg';
  } | null>(null);

  const closeScratchPalette = React.useCallback(() => setScratchPalette(null), []);

  const handleBlur = React.useCallback(
    (r: number, c: number, v: string) => {
      const gridForEval = normalizedGrid.map((row, ri) => row.map((cell, ci) => (ri === r && ci === c ? v : cell)));
      const formatted = formatCellAfterBlur(gridForEval, r, c, v);
      if (formatted !== v) setCell(r, c, formatted);
    },
    [normalizedGrid, setCell]
  );

  const applySelected = React.useCallback(() => {
    if (!selected) return;
    const raw = normalizedGrid[selected.r]?.[selected.c]?.trim() ?? '';
    if (!raw) return;
    let n: number | null;
    if (raw.startsWith('=')) {
      n = evaluateScratchFormula(normalizedGrid, raw, selected.r, selected.c);
    } else {
      n = parseLoosePlDecimal(raw);
    }
    if (n === null) return;
    const formatted = n.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    onApplyCenaJedn(formatted);
  }, [selected, normalizedGrid, onApplyCenaJedn]);

  const canApply = React.useMemo(() => {
    if (!selected) return false;
    const raw = normalizedGrid[selected.r]?.[selected.c]?.trim() ?? '';
    if (!raw) return false;
    if (raw.startsWith('=')) {
      return evaluateScratchFormula(normalizedGrid, raw, selected.r, selected.c) !== null;
    }
    return parseLoosePlDecimal(raw) !== null;
  }, [selected, normalizedGrid]);

  const toolbarDisabled = !rangeAnchor || !rangeEnd;

  const formulaRefLabel = React.useMemo(() => {
    if (!rangeAnchor || !rangeEnd) return '—';
    return formatRangeA1(rangeAnchor, rangeEnd);
  }, [rangeAnchor, rangeEnd]);

  const formulaBarValue = selected ? (normalizedGrid[selected.r]?.[selected.c] ?? '') : '';

  const handleFormulaBarChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!selected) return;
      setCell(selected.r, selected.c, e.target.value);
    },
    [selected, setCell]
  );

  const handleFormulaBarBlur = React.useCallback(
    (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (!selected) return;
      const v = e.target.value;
      const formatted = formatCellAfterBlur(normalizedGrid, selected.r, selected.c, v);
      if (formatted !== v) setCell(selected.r, selected.c, formatted);
    },
    [selected, normalizedGrid, setCell]
  );

  const handleFormulaBarKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        (e.target as HTMLInputElement).blur();
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        (e.target as HTMLInputElement).blur();
        focusSheetSoon();
      }
    },
    [focusSheetSoon]
  );

  const switchSheetTab = React.useCallback(
    (id: ScratchSheetId) => {
      if (id === activeSheet) return;
      setActiveSheet(id);
      setSelected(null);
      setRangeAnchor(null);
      setRangeEnd(null);
      setSelectionOrigin(null);
      setEditingCell(null);
      setSumaPreviewBounds(null);
      dragSelectRef.current = null;
      setIsRangeDragging(false);
    },
    [activeSheet]
  );

  const toolbarIconSx = {
    p: 0.5,
    borderRadius: '4px',
    border: '1px solid #dadce0',
    color: '#202124',
    '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
    '&.Mui-disabled': { opacity: 0.35 },
  } as const;

  return (
    <Box
      data-scratchpad-root
      sx={{
        position: 'relative',
        width: panelWidth,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        borderLeft: '1px solid #dadce0',
        bgcolor: '#fafafa',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      <Box
        onMouseDown={startPanelResize}
        role="separator"
        aria-orientation="vertical"
        aria-label="Zmień szerokość panelu obliczeń"
        title="Przeciągnij, aby zmienić szerokość panelu"
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 5,
          marginLeft: '-3px',
          cursor: 'col-resize',
          zIndex: 3,
          '&:hover': { bgcolor: 'rgba(26, 115, 232, 0.12)' },
        }}
      />
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 0.5,
          flexWrap: 'wrap',
          py: 0.5,
          px: 0.5,
          pr: 0.25,
          borderBottom: '1px solid #e8eaed',
          bgcolor: '#fff',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexWrap: 'wrap', minWidth: 0 }}>
          <Tooltip title="Pogrubienie (zaznaczone komórki)">
            <span>
              <IconButton
                size="small"
                disabled={toolbarDisabled}
                onClick={toggleBold}
                aria-label="Pogrubienie"
                sx={toolbarIconSx}
              >
                <ScratchMaterialIcon name="format_bold" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Kursywa">
            <span>
              <IconButton size="small" disabled={toolbarDisabled} onClick={toggleItalic} aria-label="Kursywa" sx={toolbarIconSx}>
                <ScratchMaterialIcon name="format_italic" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Podkreślenie">
            <span>
              <IconButton
                size="small"
                disabled={toolbarDisabled}
                onClick={toggleUnderline}
                aria-label="Podkreślenie"
                sx={toolbarIconSx}
              >
                <ScratchMaterialIcon name="format_underlined" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Kolor tła komórki">
            <span>
              <IconButton
                size="small"
                disabled={toolbarDisabled}
                onClick={(e) => setScratchPalette({ anchor: e.currentTarget, mode: 'bg' })}
                aria-label="Kolor tła zaznaczonych komórek"
                aria-haspopup="true"
                aria-expanded={scratchPalette?.mode === 'bg'}
                sx={toolbarIconSx}
              >
                <ScratchMaterialIcon name="format_color_fill" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Kolor tekstu">
            <span>
              <IconButton
                size="small"
                disabled={toolbarDisabled}
                onClick={(e) => setScratchPalette({ anchor: e.currentTarget, mode: 'fg' })}
                aria-label="Kolor tekstu zaznaczonych komórek"
                aria-haspopup="true"
                aria-expanded={scratchPalette?.mode === 'fg'}
                sx={toolbarIconSx}
              >
                <ScratchMaterialIcon name="format_color_text" />
              </IconButton>
            </span>
          </Tooltip>
          <Popover
            open={Boolean(scratchPalette)}
            anchorEl={scratchPalette?.anchor ?? null}
            onClose={closeScratchPalette}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{
              elevation: 8,
              sx: {
                p: 1.25,
                borderRadius: '8px',
                border: '1px solid #e8eaed',
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
              },
            }}
          >
            <Box>
              <Box
                component="button"
                type="button"
                onClick={() => {
                  if (scratchPalette?.mode === 'bg') clearBgColor();
                  else clearFgColor();
                  closeScratchPalette();
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  width: '100%',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '13px',
                  color: '#202124',
                  py: 0.5,
                  px: 0.25,
                  borderRadius: '4px',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                }}
              >
                <ScratchMaterialIcon name="format_clear" fontSize={18} />
                Resetuj
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(6, 1fr)',
                  gap: '8px',
                  mt: 1,
                  maxWidth: 220,
                }}
              >
                {SCRATCH_COLOR_PALETTE.map((hex) => (
                  <Box
                    key={hex}
                    component="button"
                    type="button"
                    title={hex}
                    aria-label={`Ustaw kolor ${hex}`}
                    onClick={() => {
                      if (scratchPalette?.mode === 'bg') setBgColor(hex);
                      else setFgColor(hex);
                      closeScratchPalette();
                    }}
                    sx={{
                      width: 26,
                      height: 26,
                      borderRadius: '50%',
                      bgcolor: hex,
                      border:
                        hex.toLowerCase() === '#ffffff' ? '1px solid #dadce0' : '1px solid rgba(0,0,0,0.06)',
                      cursor: 'pointer',
                      p: 0,
                      minWidth: 0,
                      boxSizing: 'border-box',
                      transition: 'box-shadow 0.12s ease',
                      '&:hover': { boxShadow: '0 0 0 2px #1a73e8' },
                      '&:focus-visible': { outline: '2px solid #1a73e8', outlineOffset: 2 },
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Popover>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
          {activeSheet === 'knr' && (
            <Tooltip title="Wyczyść arkusz KNR i pobierz nakłady z katalogu ponownie (wg Podstawy)">
              <span>
                <IconButton
                  size="small"
                  disabled={!parseKnrAndSymbolFromPodstawa(podstawaLine.trim())}
                  onClick={reloadKnrSheetFromCatalog}
                  aria-label="Odśwież arkusz KNR z bazy"
                  sx={{ ...toolbarIconSx, color: '#1a73e8', borderColor: '#aecbfa' }}
                >
                  <ScratchMaterialIcon name="sync" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <IconButton
            size="small"
            aria-label={`Zamknij panel obliczeń (Lp. ${lpLabel})`}
            onClick={onClose}
            sx={{ color: '#5F6368' }}
          >
            <ScratchMaterialIcon name="close" fontSize={20} />
          </IconButton>
        </Box>
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 0.75,
          py: 0.5,
          borderBottom: '1px solid #e8eaed',
          bgcolor: '#fff',
          minHeight: 36,
        }}
      >
        <Box
          component="span"
          aria-live="polite"
          sx={{
            flex: '0 0 auto',
            minWidth: 44,
            maxWidth: 96,
            px: 0.75,
            py: 0.35,
            border: '1px solid #dadce0',
            borderRadius: '4px',
            bgcolor: '#f8f9fa',
            fontFamily: 'Roboto, Arial, sans-serif',
            fontSize: '12px',
            fontWeight: 500,
            color: '#202124',
            textAlign: 'center',
            lineHeight: 1.3,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={formulaRefLabel}
        >
          {formulaRefLabel}
        </Box>
        <Box
          aria-hidden
          sx={{
            flex: '0 0 auto',
            display: 'flex',
            alignItems: 'baseline',
            gap: 0.25,
            userSelect: 'none',
          }}
        >
          <Box
            component="span"
            sx={{
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontStyle: 'italic',
              fontSize: '15px',
              fontWeight: 500,
              color: '#5f6368',
              lineHeight: 1,
            }}
          >
            ƒ<sub style={{ fontSize: '10px', opacity: 0.85 }}>x</sub>
          </Box>
          <Box component="span" sx={{ color: '#9aa0a6', fontSize: '13px', fontFamily: 'Roboto, sans-serif' }}>
            =
          </Box>
        </Box>
        <InputBase
          fullWidth
          size="small"
          inputRef={formulaBarInputRef}
          value={formulaBarValue}
          onChange={handleFormulaBarChange}
          onBlur={handleFormulaBarBlur}
          onKeyDown={handleFormulaBarKeyDown}
          onFocus={() => setEditingCell(null)}
          disabled={!selected}
          placeholder={selected ? '' : 'Zaznacz komórkę'}
          aria-label="Pasek formuły (treść aktywnej komórki)"
          inputProps={{ spellCheck: false, autoComplete: 'off' }}
          sx={{
            flex: '1 1 auto',
            minWidth: 0,
            border: '1px solid #dadce0',
            borderRadius: '4px',
            bgcolor: '#fff',
            '& .MuiInputBase-input': {
              py: 0.5,
              px: 0.75,
              fontSize: '13px',
              fontFamily: 'Roboto, Arial, sans-serif',
            },
            '&.Mui-disabled': { bgcolor: '#f1f3f4' },
          }}
        />
      </Box>

      <TableContainer
        ref={sheetRef}
        tabIndex={0}
        onKeyDown={handleSheetKeyDown}
        onCopy={handleScratchCopy}
        onCut={handleScratchCut}
        onPaste={handleScratchPaste}
        sx={{
          flex: '1 1 auto',
          minHeight: 0,
          overflow: 'auto',
          px: 0.75,
          pb: 0.5,
          '&:focus': { outline: 'none' },
          '&:focus-visible': { outline: '2px solid #1a73e8', outlineOffset: -2 },
        }}
      >
        <Table
          size="small"
          stickyHeader
          sx={{
            tableLayout: 'fixed',
            width: '100%',
            borderCollapse: 'collapse',
            borderSpacing: 0,
            '& .MuiTableCell-root': {
              boxSizing: 'border-box',
              border: `1px solid ${GRID_LINE}`,
              padding: 0,
            },
            '& thead .MuiTableCell-head': {
              backgroundClip: 'padding-box',
            },
          }}
        >
          <colgroup>
            <col style={{ width: ROW_GUTTER_PX }} />
            {colWidths.map((w, ci) => (
              <col key={COL_LABELS[ci]} style={{ width: w }} />
            ))}
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  width: ROW_GUTTER_PX,
                  minHeight: 24,
                  fontFamily: 'Roboto, Arial, sans-serif',
                  fontWeight: 600,
                  fontSize: '11px',
                  bgcolor: HEADER_BG,
                  color: '#5f6368',
                  textAlign: 'center',
                  verticalAlign: 'middle',
                }}
              />
              {COL_LABELS.map((lab, ci) => (
                <TableCell
                  key={lab}
                  align="center"
                  sx={{
                    position: 'relative',
                    minHeight: 24,
                    fontFamily: 'Roboto, Arial, sans-serif',
                    fontWeight: 600,
                    fontSize: '11px',
                    bgcolor: HEADER_BG,
                    color: '#202124',
                    verticalAlign: 'middle',
                    width: colWidths[ci],
                  }}
                >
                  {lab}
                  {ci < SCRATCH_COLS - 1 ? (
                    <Box
                      onMouseDown={(e) => startColResize(ci, e)}
                      role="separator"
                      aria-orientation="vertical"
                      aria-label={`Zmień szerokość kolumn ${lab} i ${COL_LABELS[ci + 1]}`}
                      title="Przeciągnij, aby zmienić szerokość kolumn"
                      sx={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: 5,
                        marginRight: '-3px',
                        cursor: 'col-resize',
                        zIndex: 2,
                        '&:hover': { bgcolor: 'rgba(26, 115, 232, 0.15)' },
                      }}
                    />
                  ) : null}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {normalizedGrid.map((row, r) => (
              <TableRow key={r}>
                <TableCell
                  sx={{
                    width: ROW_GUTTER_PX,
                    minHeight: 26,
                    fontFamily: 'Roboto, Arial, sans-serif',
                    fontSize: '11px',
                    fontWeight: 400,
                    color: '#5f6368',
                    bgcolor: CELL_BG,
                    textAlign: 'center',
                    verticalAlign: 'middle',
                  }}
                >
                  {r + 1}
                </TableCell>
                {row.map((cell, c) => {
                  const st = normalizedCellStyles[r]?.[c] ?? {};
                  const inRange = isInRangeRect(r, c);
                  const isEditingHere = editingCell?.r === r && editingCell?.c === c;
                  const inSumaPreview =
                    sumaPreviewBounds != null &&
                    r >= sumaPreviewBounds.r0 &&
                    r <= sumaPreviewBounds.r1 &&
                    c >= sumaPreviewBounds.c0 &&
                    c <= sumaPreviewBounds.c1;
                  const inFillGrabPreview =
                    fillPreviewBounds != null &&
                    sumaPreviewBounds == null &&
                    r >= fillPreviewBounds.r0 &&
                    r <= fillPreviewBounds.r1 &&
                    c >= fillPreviewBounds.c0 &&
                    c <= fillPreviewBounds.c1;
                  const cellBg =
                    st.backgroundColor ||
                    (inSumaPreview ? SUMA_REF_FILL : inFillGrabPreview ? 'rgba(26, 115, 232, 0.09)' : inRange ? RANGE_FILL : CELL_BG);
                  const isMulti =
                    inRange &&
                    selectionBounds != null &&
                    (selectionBounds.r0 !== selectionBounds.r1 || selectionBounds.c0 !== selectionBounds.c1);
                  const isRangeOrigin =
                    isMulti &&
                    selectionOrigin != null &&
                    selectionOrigin.r === r &&
                    selectionOrigin.c === c;
                  const selectionShadowMain = inSumaPreview
                    ? sumaPreviewBoxShadow(r, c, sumaPreviewBounds)
                    : inRange && selectionBounds
                      ? sheetsRangeBoxShadow(r, c, selectionBounds, selectionOrigin, Boolean(isMulti))
                      : 'none';
                  const fillGrabShadow =
                    inFillGrabPreview && fillPreviewBounds
                      ? fillGrabPreviewBoxShadow(r, c, fillPreviewBounds)
                      : 'none';
                  const selectionShadowCombined =
                    fillGrabShadow !== 'none' && selectionShadowMain !== 'none'
                      ? `${fillGrabShadow}, ${selectionShadowMain}`
                      : fillGrabShadow !== 'none'
                        ? fillGrabShadow
                        : selectionShadowMain;
                  const showFillHandle =
                    selectionBounds != null &&
                    sumaPreviewBounds == null &&
                    !editingCell &&
                    r === selectionBounds.r1 &&
                    c === selectionBounds.c1;
                  return (
                    <TableCell
                      key={c}
                      data-scratch-r={r}
                      data-scratch-c={c}
                      onDoubleClick={(e) => {
                        e.preventDefault();
                        enterEditMode(r, c, 'end');
                      }}
                      sx={{
                        width: colWidths[c],
                        minHeight: 26,
                        verticalAlign: 'middle',
                        cursor: 'cell',
                        bgcolor: cellBg,
                        position: 'relative',
                        zIndex: showFillHandle
                          ? 5
                          : inSumaPreview
                            ? 3
                            : isRangeOrigin
                              ? 2
                              : inRange
                                ? 1
                                : 0,
                        boxShadow: selectionShadowCombined,
                        borderRadius: 0,
                      }}
                    >
                      <input
                        ref={(el) => setInputRef(r, c, el)}
                        type="text"
                        value={isEditingHere ? cell : getScratchCellDisplayString(normalizedGrid, r, c)}
                        readOnly={!isEditingHere}
                        tabIndex={-1}
                        data-scratch-r={r}
                        data-scratch-c={c}
                        onMouseDown={(e) => handleInputMouseDown(r, c, e)}
                        onChange={(e) => setCell(r, c, e.target.value)}
                        onBlur={(e) => {
                          handleBlur(r, c, e.target.value);
                          setEditingCell((cur) => (cur?.r === r && cur?.c === c ? null : cur));
                        }}
                        onKeyDown={(e) => handleScratchCellKeyDown(r, c, e)}
                        aria-label={`Komórka ${COL_LABELS[c]}${r + 1}`}
                        style={{
                          ...baseSheetCellInputSx,
                          ...mergeInputStyle(st, isEditingHere),
                          userSelect: isRangeDragging ? 'none' : 'text',
                        }}
                        autoComplete="off"
                        spellCheck={false}
                      />
                      {showFillHandle ? (
                        <Box
                          onMouseDown={handleFillHandleMouseDown}
                          role="presentation"
                          title="Przeciągnij, aby wypełnić komórki"
                          aria-label="Uchwyt wypełnienia — przeciągnij, aby skopiować zawartość zaznaczenia"
                          sx={{
                            position: 'absolute',
                            right: -4,
                            bottom: -4,
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: SELECTION_OUTLINE,
                            border: '1px solid #fff',
                            boxSizing: 'border-box',
                            cursor: 'crosshair',
                            zIndex: 12,
                            pointerEvents: 'auto',
                            boxShadow: '0 0 0 1px rgba(26, 115, 232, 0.35)',
                            '&:hover': {
                              bgcolor: '#1557b0',
                              transform: 'scale(1.1)',
                            },
                          }}
                        />
                      ) : null}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        role="tablist"
        aria-label="Arkusze obliczeń"
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'flex-end',
          flexWrap: 'nowrap',
          gap: 0,
          minHeight: 34,
          px: 0.5,
          pt: 0.25,
          bgcolor: '#e8eaed',
          borderTop: '1px solid #dadce0',
        }}
      >
        {SCRATCH_SHEET_TABS.map(({ id, label }, idx) => {
          const active = activeSheet === id;
          return (
            <Box
              key={id}
              component="button"
              type="button"
              role="tab"
              aria-selected={active}
              tabIndex={active ? 0 : -1}
              onClick={() => switchSheetTab(id)}
              sx={{
                position: 'relative',
                flex: '1 1 0',
                minWidth: 0,
                py: 0.65,
                px: 0.75,
                mb: '-1px',
                border: '1px solid',
                borderColor: active ? '#dadce0' : 'transparent',
                borderBottom: active ? '1px solid #fff' : '1px solid transparent',
                bgcolor: active ? '#fff' : 'transparent',
                color: active ? '#1a73e8' : '#5f6368',
                borderRadius: '8px 8px 0 0',
                ml: idx > 0 ? '2px' : 0,
                cursor: 'pointer',
                fontFamily: 'Roboto, Arial, sans-serif',
                fontSize: '13px',
                fontWeight: active ? 500 : 400,
                lineHeight: 1.2,
                textAlign: 'center',
                textTransform: 'none',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                zIndex: active ? 1 : 0,
                boxShadow: active ? '0 -1px 2px rgba(60,64,67,0.15)' : 'none',
                '&:hover': {
                  bgcolor: active ? '#fff' : 'rgba(0,0,0,0.06)',
                },
                '&:focus-visible': {
                  outline: '2px solid #1a73e8',
                  outlineOffset: 1,
                },
              }}
            >
              {label}
            </Box>
          );
        })}
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          px: 1.25,
          pt: 0.75,
          pb: 0.5,
          borderTop: '1px solid #dadce0',
          bgcolor: '#fff',
        }}
      >
        <Button
          fullWidth
          variant="contained"
          disabled={!canApply}
          onClick={applySelected}
          sx={{
            textTransform: 'none',
            fontFamily: 'Roboto, sans-serif',
            fontSize: '13px',
            bgcolor: '#2020AF',
            '&:hover': { bgcolor: '#18188c' },
            '&.Mui-disabled': { bgcolor: '#e0e0e0', color: '#9e9e9e' },
          }}
        >
          Wstaw zaznaczoną komórkę jako cenę jedn.
        </Button>
      </Box>
    </Box>
  );
};

export default PozycjaCalcScratchpad;
